import { useState, type FormEvent } from "react";
import { useAPIKeys } from "./useAPIKeys";

function scopeLabel(canPlaceCalls: boolean, canReadReports: boolean): string {
  const parts: string[] = [];
  if (canPlaceCalls) parts.push("place calls");
  if (canReadReports) parts.push("read reports");
  return parts.length > 0 ? parts.join(", ") : "no capabilities";
}

/**
 * Tenant-admin panel: issue, list and revoke API keys for third-party
 * integrations — the credential a company's own system authenticates with
 * (Authorization: Bearer <token>) against /v1/external/calls to place
 * calls and fetch reporting. A key's capabilities are exactly the two
 * checkboxes ticked here, never implied by the issuing admin's own role —
 * see tenant.RoleAPIKey's own doc comment for why.
 */
export function DeveloperPanel() {
  const { apiKeys, loading, error, lastCreated, create, revoke, dismissLastCreated } = useAPIKeys();
  const [label, setLabel] = useState("");
  const [canPlaceCalls, setCanPlaceCalls] = useState(false);
  const [canReadReports, setCanReadReports] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const ok = await create(label, canPlaceCalls, canReadReports);
    setSubmitting(false);
    if (ok) setLabel("");
  };

  return (
    <section aria-labelledby="developer-heading" className="card">
      <h2 id="developer-heading">Developer</h2>
      <p className="card__note">
        Issue an API key for a third-party integration. A key can place calls and/or fetch call
        reporting over HTTP, scoped to exactly the capabilities you tick below — nothing more.
        Requests are rate-limited per key.
      </p>

      {lastCreated ? (
        <div className="invites__created" role="status">
          <p>
            Created <strong>{lastCreated.label}</strong> ({scopeLabel(lastCreated.can_place_calls, lastCreated.can_read_reports)}
            ). Copy this key now — it is shown only this once:
          </p>
          <code className="invites__link">{lastCreated.token}</code>
          <button type="button" onClick={dismissLastCreated}>
            Dismiss
          </button>
        </div>
      ) : null}

      <form className="invites__form" onSubmit={(event) => void handleSubmit(event)}>
        <label className="field">
          <span>Label</span>
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="e.g. Acme Corp integration"
            required
          />
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={canPlaceCalls}
            onChange={(event) => setCanPlaceCalls(event.target.checked)}
          />
          <span>Can place calls</span>
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={canReadReports}
            onChange={(event) => setCanReadReports(event.target.checked)}
          />
          <span>Can read reports</span>
        </label>
        <button type="submit" disabled={submitting || (!canPlaceCalls && !canReadReports)}>
          {submitting ? "Creating…" : "Create key"}
        </button>
      </form>

      {error ? <p className="card__error">{error}</p> : null}

      {loading ? (
        <p className="card__note">Loading…</p>
      ) : apiKeys.length === 0 ? (
        <p className="card__note">No API keys yet.</p>
      ) : (
        <ul className="invites__list">
          {apiKeys.map((key) => (
            <li key={key.api_key_id} className="invites__item">
              <span>
                {key.label} · {scopeLabel(key.can_place_calls, key.can_read_reports)}
                {key.revoked_at ? " · revoked" : ""}
                {!key.revoked_at && key.last_used_at
                  ? ` · last used ${new Date(key.last_used_at).toLocaleString()}`
                  : ""}
                {!key.revoked_at && !key.last_used_at ? " · never used" : ""}
              </span>
              {!key.revoked_at ? (
                <button type="button" onClick={() => void revoke(key.api_key_id)}>
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
